import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { 
  Box, AppBar, Toolbar, Typography, Button, CircularProgress
} from '@mui/material';
import Logo from './components/Logo';
import Favicon from './components/Favicon';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AdaptiveLayout from './components/Layout/AdaptiveLayout';
import { useResponsive } from './hooks/useMediaQuery';
import './styles/mobile-first.css';

// Import components
import Dashboard from './components/Dashboard';
import ChecklistForm from './components/ChecklistForm';
import ChecklistDetail from './components/ChecklistDetail';
import ExpenseReportForm from './components/ExpenseReportForm';
import MaintenanceInvoiceSection from './components/MaintenanceInvoiceSection';
import EmailSettings from './components/EmailSettings';
import ManageUsers from './components/ManageUsers';
import LoginForm from './components/LoginForm';
import RegistrationForm from './components/RegistrationForm';
import EmailVerification from './components/EmailVerification';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PrivateRoute from './components/PrivateRoute';
import SafetyLetterManagement from './components/admin/SafetyLetterManagement';
import BankStatementManager from './components/admin/BankStatementManager';
import ExpenseReportReviewer from './components/admin/ExpenseReportReviewer';
import VehicleInspectionReviewer from './components/admin/VehicleInspectionReviewer';
import ChecklistPrintView from './components/admin/ChecklistPrintView';
import SafetyLetterPrintView from './components/admin/SafetyLetterPrintView';
import SettingsSection from './components/admin/SettingsSection';
import DriverRegisterList from './components/admin/DriverRegisterList';
import VehicleDetail from './components/admin/VehicleDetail';
import VehicleEdit from './components/admin/VehicleEdit';
import MaintenanceInvoiceManager from './components/admin/MaintenanceInvoiceManager';
import PreferredBranchSettings from './components/admin/PreferredBranchSettings';
import ServiceOpsUserManager from './components/admin/ServiceOpsUserManager';
import WebPortalAllocationSettings from './components/admin/WebPortalAllocationSettings';
import ImpersonationIndicator from './components/ImpersonationIndicator';
import AdminActivityLogs from './components/AdminActivityLogs';

// Import mobile components
import MobileDashboard from './components/mobile/MobileDashboard';
import MobileChecklistForm from './components/mobile/MobileChecklistForm';
import MobileExpenseReportForm from './components/mobile/MobileExpenseReportForm';

const Navigation = () => {
  const { user, logout } = useAuth();
  const [userViewMode, setUserViewMode] = useState(false);
  const { isMobile: isResponsiveMobile, isTablet } = useResponsive();
  
  // Load user view preference from localStorage
  useEffect(() => {
    if (user?.is_admin) {
      const savedViewMode = localStorage.getItem('adminUserViewMode');
      if (savedViewMode === 'true') {
        setUserViewMode(true);
      }
    }
  }, [user]);
  
  // Update localStorage when view mode changes
  const handleViewModeChange = (newMode) => {
    setUserViewMode(newMode);
    localStorage.setItem('adminUserViewMode', newMode);
    
    // Trigger storage event for other components to detect the change
    window.dispatchEvent(new Event('storage'));
  };

  if (!user) return null;
  
  // Don't render desktop navigation on mobile - handled by MobileNavigation
  if (isResponsiveMobile || isTablet) return null;

  const navItems = [
    { label: 'Dashboard', path: '/' }
  ];

  // User-specific navigation items
  if (!user.is_admin || userViewMode) {
    navItems.push(
      { label: 'New Checklist', path: '/new-checklist' },
      { label: 'Maintenance Invoices', path: '/maintenance-invoices' },
      { label: 'Expense Report', path: '/expense-report' }
    );
  }

  // Admin-specific navigation items
  if (user.is_admin && !userViewMode) {
    navItems.push(
      { label: 'Settings', path: '/admin/settings' },
      { label: 'Maintenance Invoices', path: '/admin/maintenance-invoices' },
      { label: 'Bank Statements', path: '/admin/bank-statements' },
      { label: 'Safety Letters', path: '/admin/safety-letters' },
      { label: 'Vehicle Inspections', path: '/admin/vehicle-inspections' },
      { label: 'Expense Reports', path: '/admin/expense-reports' }
    );
  }

  return (
    <AppBar position="static">
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box sx={{ mr: 2 }}>
            <Logo height="40px" />
          </Box>
          <Typography variant="h6" component="div" noWrap>
            Vehicle Maintenance App
            {user.is_admin && userViewMode && (
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  ml: 1, 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  py: 0.25, 
                  px: 1, 
                  borderRadius: 1 
                }}
              >
                User View
              </Box>
            )}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex' }}>
          {navItems.map((item) => (
            <Button key={item.label} color="inherit" component={Link} to={item.path}>
              {item.label}
            </Button>
          ))}
          
          {user.is_admin && (
            <Button 
              color="inherit" 
              onClick={() => handleViewModeChange(!userViewMode)}
              sx={{ 
                bgcolor: userViewMode ? 'rgba(255,255,255,0.15)' : 'transparent',
                ml: 1
              }}
            >
              {userViewMode ? "Admin View" : "User View"}
            </Button>
          )}
          
          <Button color="inherit" onClick={logout} sx={{ ml: 1 }}>Logout</Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const DashboardComponent = () => {
  const { isMobile, isTablet } = useResponsive();
  if (isMobile || isTablet) {
    return <MobileDashboard />;
  }
  return <Dashboard />;
};

const ChecklistFormComponent = () => {
  const { isMobile, isTablet } = useResponsive();
  if (isMobile || isTablet) {
    return <MobileChecklistForm />;
  }
  return <ChecklistForm />;
};

const ExpenseReportFormComponent = () => {
  const { isMobile, isTablet } = useResponsive();
  if (isMobile || isTablet) {
    return <MobileExpenseReportForm />;
  }
  return <ExpenseReportForm />;
};

const AppContent = () => {
  const { user, loading } = useAuth();
  const { isMobile, isTablet } = useResponsive();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AdaptiveLayout>
      <Favicon />
      <Navigation />
      <ImpersonationIndicator />
      <Box sx={{ 
        p: (isMobile || isTablet) ? 0 : 3,
        height: '100%'
      }}>
        <Routes>
          {/* Root route with auth-based redirect */}
          <Route path="/" element={
            user ? <DashboardComponent /> : <Navigate to="/login" replace />
          } />
          
          {/* Public routes */}
          <Route path="/login" element={
            !user ? <LoginForm /> : <Navigate to="/" replace />
          } />
          <Route path="/register" element={
            !user ? <RegistrationForm /> : <Navigate to="/" replace />
          } />
          <Route path="/verify-email" element={
            !user ? <EmailVerification /> : <Navigate to="/" replace />
          } />
          <Route path="/reset-password" element={
            !user ? <ResetPassword /> : <Navigate to="/" replace />
          } />
          <Route path="/forgot-password" element={
            !user ? <ForgotPassword /> : <Navigate to="/" replace />
          } />
          
          {/* Protected routes */}
          <Route path="/new-checklist" element={
            <PrivateRoute userOnly={true}>
              <ChecklistFormComponent />
            </PrivateRoute>
          } />
          <Route path="/checklist/:id" element={
            <PrivateRoute>
              <ChecklistDetail />
            </PrivateRoute>
          } />
          {/* ADD THIS ROUTE - Maintenance Invoices Page */}
          <Route path="/maintenance-invoices" element={
            <PrivateRoute userOnly={true}>
              <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                <Typography variant="h4" component="h1" gutterBottom>
                  Maintenance Invoices
                </Typography>
                <MaintenanceInvoiceSection />
              </Box>
            </PrivateRoute>
          } />
          <Route path="/expense-report" element={
            <PrivateRoute userOnly={true}>
              <ExpenseReportFormComponent />
            </PrivateRoute>
          } />
          <Route path="/manage-users" element={
            <PrivateRoute adminOnly={true}>
              <ManageUsers />
            </PrivateRoute>
          } />
          <Route path="/email-settings" element={
            <PrivateRoute adminOnly={true}>
              <EmailSettings />
            </PrivateRoute>
          } />
          <Route path="/safety-letters" element={
            <PrivateRoute adminOnly={true}>
              <SafetyLetterManagement />
            </PrivateRoute>
          } />
          <Route path="/bank-statements" element={
            <PrivateRoute adminOnly={true}>
              <BankStatementManager />
            </PrivateRoute>
          } />
          <Route path="/expense-reports" element={
            <PrivateRoute adminOnly={true}>
              <ExpenseReportReviewer />
            </PrivateRoute>
          } />
          <Route path="/admin/maintenance-invoices" element={
            <PrivateRoute adminOnly={true}>
              <MaintenanceInvoiceManager />
            </PrivateRoute>
          } />
          <Route path="/admin/settings" element={
            <PrivateRoute adminOnly={true}>
              <SettingsSection />
            </PrivateRoute>
          } />
          <Route path="/admin/driver-register" element={
            <PrivateRoute adminOnly={true}>
              <DriverRegisterList />
            </PrivateRoute>
          } />
          <Route path="/admin/driver-register/:number" element={
            <PrivateRoute adminOnly={true}>
              <VehicleDetail />
            </PrivateRoute>
          } />
          <Route path="/admin/driver-register/:number/edit" element={
            <PrivateRoute adminOnly={true}>
              <VehicleEdit />
            </PrivateRoute>
          } />
          <Route path="/admin/preferred-branch" element={
            <PrivateRoute adminOnly={true}>
              <PreferredBranchSettings />
            </PrivateRoute>
          } />
          <Route path="/admin/web-portal-allocation" element={
            <PrivateRoute adminOnly={true}>
              <WebPortalAllocationSettings />
            </PrivateRoute>
          } />
          <Route path="/admin/safety-letters" element={
            <PrivateRoute adminOnly={true}>
              <SafetyLetterManagement />
            </PrivateRoute>
          } />
          <Route path="/admin/bank-statements" element={
            <PrivateRoute adminOnly={true}>
              <BankStatementManager />
            </PrivateRoute>
          } />
          <Route path="/admin/expense-reports" element={
            <PrivateRoute adminOnly={true}>
              <ExpenseReportReviewer />
            </PrivateRoute>
          } />
          <Route path="/admin/vehicle-inspections" element={
            <PrivateRoute adminOnly={true}>
              <VehicleInspectionReviewer />
            </PrivateRoute>
          } />
          <Route path="/admin/print/checklist/:checklistId" element={
            <PrivateRoute adminOnly={true}>
              <ChecklistPrintView />
            </PrivateRoute>
          } />
          <Route path="/admin/print/safety-letter/:safetyLetterId/user/:userId" element={
            <PrivateRoute adminOnly={true}>
              <SafetyLetterPrintView />
            </PrivateRoute>
          } />
          <Route path="/admin/service-ops-users" element={
            <PrivateRoute adminOnly={true}>
              <ServiceOpsUserManager />
            </PrivateRoute>
          } />
          
          {/* Secret Admin Activity Logs Route - No links from app */}
          <Route path="/admin-activity" element={
            <PrivateRoute adminOnly={true}>
              <AdminActivityLogs />
            </PrivateRoute>
          } />
        </Routes>
      </Box>
    </AdaptiveLayout>
  );
};

const App = () => {
  // Create responsive theme with mobile-first optimizations
  const theme = responsiveFontSizes(createTheme({
    palette: {
      primary: {
        main: '#3498db',
      },
      secondary: {
        main: '#2c3e50',
      },
    },
    components: {
      // Mobile-optimized button styling
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            minHeight: 44, // Touch-friendly minimum
            fontSize: '16px', // Prevent zoom on iOS
            fontWeight: 600,
            padding: '12px 24px',
            '&.MuiButton-sizeLarge': {
              minHeight: 56,
              padding: '16px 32px',
              fontSize: '18px',
            },
            '&.MuiButton-sizeSmall': {
              minHeight: 36,
              padding: '8px 16px',
              fontSize: '14px',
            },
          },
        },
      },
      // Mobile-optimized input styling
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              minHeight: 48, // Touch-friendly
              fontSize: '16px', // Prevent zoom on iOS
              '& input': {
                padding: '12px 16px',
              },
            },
          },
        },
      },
      // Mobile-optimized card styling
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      // Touch-friendly checkbox styling
      MuiCheckbox: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44,
            padding: 10,
          },
        },
      },
      // Touch-friendly list item styling
      MuiListItem: {
        styleOverrides: {
          root: {
            minHeight: 48,
            padding: '12px 16px',
          },
        },
      },
      // Bottom navigation styling
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: 70,
            paddingBottom: 'env(safe-area-inset-bottom)',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 'auto',
            padding: '8px 12px',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              '&.Mui-selected': {
                fontSize: '0.75rem',
              },
            },
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768, // Updated for mobile-first
        lg: 1024,
        xl: 1200,
      },
    },
    typography: {
      // Mobile-optimized typography
      fontSize: 16, // Base font size for mobile
      h1: {
        fontSize: '2rem',
        '@media (max-width:768px)': {
          fontSize: '1.5rem',
        },
      },
      h2: {
        fontSize: '1.75rem',
        '@media (max-width:768px)': {
          fontSize: '1.25rem',
        },
      },
      h3: {
        fontSize: '1.5rem',
        '@media (max-width:768px)': {
          fontSize: '1.125rem',
        },
      },
      h4: {
        fontSize: '1.25rem',
        '@media (max-width:768px)': {
          fontSize: '1rem',
        },
      },
      h5: {
        fontSize: '1.125rem',
        '@media (max-width:768px)': {
          fontSize: '0.875rem',
        },
      },
      h6: {
        fontSize: '1rem',
        '@media (max-width:768px)': {
          fontSize: '0.875rem',
        },
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.4,
      },
      button: {
        fontSize: '1rem',
        fontWeight: 600,
      },
    },
  }));

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppContent />
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;